<template>
  <div class="panel-overlay" :class="{ open: visible }" @click.self="$emit('close')">
    <div class="panel-card profile-panel">
      <div class="panel-header">
        <span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align:-3px;margin-right:6px">
            <circle cx="12" cy="8" r="4"/><path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/>
          </svg> 个人中心
        </span>
        <button @click="$emit('close')">×</button>
      </div>
      <div class="panel-body">
        <!-- 头像区域 -->
        <div class="profile-avatar-section">
          <div class="profile-avatar-wrap" @click="triggerUpload" :title="saving ? '保存中...' : '点击更换头像'">
            <img v-if="avatarPreview" :src="avatarPreview" class="profile-avatar-img" />
            <div v-else class="profile-avatar-placeholder">{{ initialChar }}</div>
            <div class="profile-avatar-overlay">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
            </div>
          </div>
          <input ref="fileInput" type="file" accept="image/*" style="display:none" @change="onFileChange" />
          <div class="profile-avatar-hint">点击更换头像</div>
        </div>

        <!-- 资料字段 -->
        <div class="profile-fields">
          <div class="profile-field">
            <label>显示名称</label>
            <input v-model="form.display_name" type="text" maxlength="30" placeholder="输入显示名称" :disabled="saving" />
          </div>
          <div class="profile-field">
            <label>用户ID</label>
            <input v-model="form.username" type="text" disabled class="readonly" />
            <div class="field-hint">用户ID不可修改（即登录用户名）</div>
          </div>
          <div class="profile-field">
            <label>灵鹿号</label>
            <input :value="lingluId" type="text" disabled class="readonly" />
            <div class="field-hint">灵鹿号为系统分配，不可修改</div>
          </div>
          <div class="profile-field" v-if="clientName">
            <label>所属组织</label>
            <input :value="clientName" type="text" disabled class="readonly" />
          </div>
        </div>

        <button class="btn-save" :disabled="saving || !hasChanges" @click="saveProfile">
          {{ saving ? '保存中…' : '保存修改' }}
        </button>
        <div v-if="error" class="profile-error">{{ error }}</div>
        <div v-if="success" class="profile-success">{{ success }}</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue'
import { usePortal } from '@/stores/portal'
import { globalToast } from '@/composables/useToast'

const props = defineProps<{ visible: boolean }>()
const emit = defineEmits<{ close: [] }>()

const portal = usePortal()
const fileInput = ref<HTMLInputElement>()

const form = reactive({ display_name: '', username: '', avatar: '' })
const originalForm = reactive({ display_name: '', avatar: '' })
const avatarPreview = ref('')
const lingluId = ref('')
const clientName = ref('')
const saving = ref(false)
const error = ref('')
const success = ref('')

const initialChar = computed(() => {
  const name = form.display_name || form.username || '?'
  return name.charAt(0).toUpperCase()
})

const hasChanges = computed(() => {
  return form.display_name !== originalForm.display_name || form.avatar !== originalForm.avatar
})

watch(() => props.visible, async (v) => {
  if (v) {
    error.value = ''
    success.value = ''
    await loadProfile()
  }
})

async function loadProfile() {
  try {
    // silent=true：即使 401 也不触发 forceLogout，只显示错误提示
    const res = await portal.apiGet('profile.php?action=get', {}, true)
    const d = res.data || {}
    form.display_name = d.display_name || d.username || ''
    form.username = d.username || ''
    form.avatar = d.avatar || ''
    originalForm.display_name = form.display_name
    originalForm.avatar = form.avatar
    avatarPreview.value = d.avatar || ''
    lingluId.value = 'LG' + String(d.user_id || 0).padStart(6, '0')
    clientName.value = d.client_name || ''
  } catch (e: any) {
    error.value = '加载资料失败: ' + (e.message || '未知错误')
    console.error('[ProfilePanel] loadProfile error:', e)
  }
}

function triggerUpload() {
  if (saving.value) return
  fileInput.value?.click()
}

async function onFileChange(e: Event) {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return

  // 本地预览
  const reader = new FileReader()
  reader.onload = (ev) => {
    avatarPreview.value = ev.target?.result as string
  }
  reader.readAsDataURL(file)

  // 上传
  saving.value = true
  error.value = ''
  try {
    const formData = new FormData()
    formData.append('file', file)
    const token = portal.getToken()
    const res = await fetch('/api/profile.php?action=avatar', {
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + token },
      body: formData,
    })
    if (res.status === 401) throw new Error('请登录后使用')
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || '上传失败')
    form.avatar = data.avatar
    globalToast('头像已更新', 'success')
  } catch (e: any) {
    error.value = e.message
    // 恢复原头像
    avatarPreview.value = originalForm.avatar
  } finally {
    saving.value = false
    input.value = ''
  }
}

async function saveProfile() {
  if (!hasChanges.value) return
  saving.value = true
  error.value = ''
  success.value = ''
  try {
    const res = await portal.apiPost('profile.php?action=update', {
      display_name: form.display_name,
      avatar: form.avatar,
    }, true)
    if (res.error) throw new Error(res.error)
    originalForm.display_name = form.display_name
    originalForm.avatar = form.avatar
    // 同步更新全局 store，使 Sidebar 头像/名称和对话区域实时刷新
    portal.updateUserProfile(form.display_name, form.avatar)
    success.value = '保存成功'
    globalToast('资料已更新', 'success')
    setTimeout(() => { success.value = '' }, 3000)
  } catch (e: any) {
    error.value = e.message
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.panel-overlay {
  position: fixed; inset: 0; z-index: 400;
  background: rgba(0,0,0,0.3);
  display: flex; align-items: center; justify-content: center;
  opacity: 0; visibility: hidden; transition: all 0.25s;
}
.panel-overlay.open { opacity: 1; visibility: visible; }
.panel-card {
  background: var(--bg-secondary); border: 1px solid var(--border-color);
  border-radius: 16px; box-shadow: var(--shadow);
  width: 420px; max-width: 92vw; max-height: 85vh;
  display: flex; flex-direction: column;
}
.panel-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px; border-bottom: 1px solid var(--border-color);
  font-size: 15px; font-weight: 600;
}
.panel-header button {
  background: none; border: none; font-size: 22px; color: var(--text-tertiary);
  cursor: pointer; line-height: 1; padding: 0 4px;
}
.panel-header button:hover { color: var(--text-primary); }
.panel-body { padding: 20px; overflow-y: auto; }

.profile-avatar-section {
  display: flex; flex-direction: column; align-items: center; margin-bottom: 20px;
}
.profile-avatar-wrap {
  width: 80px; height: 80px; border-radius: 50%;
  overflow: hidden; cursor: pointer; position: relative;
  border: 2px solid var(--border-color);
  transition: border-color 0.2s;
}
.profile-avatar-wrap:hover { border-color: var(--accent); }
.profile-avatar-img { width: 100%; height: 100%; object-fit: cover; }
.profile-avatar-placeholder {
  width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;
  font-size: 32px; font-weight: 700; color: var(--accent);
  background: rgba(79,140,255,0.1);
}
.profile-avatar-overlay {
  position: absolute; inset: 0; background: rgba(0,0,0,0.35);
  display: flex; align-items: center; justify-content: center;
  opacity: 0; transition: opacity 0.2s; color: #fff;
}
.profile-avatar-wrap:hover .profile-avatar-overlay { opacity: 1; }
.profile-avatar-hint { font-size: 12px; color: var(--text-tertiary); margin-top: 8px; }

.profile-fields { display: flex; flex-direction: column; gap: 14px; margin-bottom: 20px; }
.profile-field { display: flex; flex-direction: column; gap: 4px; }
.profile-field label { font-size: 13px; font-weight: 600; color: var(--text-secondary); }
.profile-field input {
  padding: 8px 12px; border: 1px solid var(--border-color); border-radius: 8px;
  font-size: 14px; background: var(--bg-primary); color: var(--text-primary);
  outline: none; transition: border-color 0.15s;
}
.profile-field input:focus:not(.readonly) { border-color: var(--accent); }
.profile-field input.readonly { background: var(--bg-hover); color: var(--text-tertiary); cursor: not-allowed; }
.field-hint { font-size: 11px; color: var(--text-tertiary); }

.btn-save {
  width: 100%; padding: 10px; border: none; border-radius: 8px;
  background: var(--accent); color: #fff; font-size: 14px; font-weight: 600;
  cursor: pointer; transition: background 0.15s;
}
.btn-save:hover:not(:disabled) { background: var(--accent-hover); }
.btn-save:disabled { opacity: 0.5; cursor: not-allowed; }
.profile-error { color: var(--danger); font-size: 13px; margin-top: 8px; text-align: center; }
.profile-success { color: var(--success); font-size: 13px; margin-top: 8px; text-align: center; }
</style>
